import NavLogo from "@/assets/logos/logo-simple-lucidity.svg";
import { loginPopup } from "@/Atoms";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import {
  BookmarksSimple,
  ChatsCircle,
  Fire,
  Gauge,
  Heart,
  NewspaperClipping,
  PuzzlePiece,
  RocketLaunch,
  Ticket,
  TipJar,
} from "@phosphor-icons/react/dist/ssr";
import { useAtom } from "jotai";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";

const tools = [
  {
    name: "Investor & Angel Database",
    description:
      "Use our Investor Search Tool to find the Right Investor for You",
    icon: <TipJar weight='duotone' className='text-orange-700 size-7' />,
    url: "/investors",
  },
  {
    name: "Startup Database",
    description: "Explore Startups across the Middle East & Africa",
    icon: <RocketLaunch weight='duotone' className='text-orange-700 size-7' />,
    url: "/startups",
  },
  {
    name: "My Messages",
    description:
      "Check your inbox to see if you’ve got any messages from other startup founders and investors",
    icon: <ChatsCircle weight='duotone' className='text-orange-700 size-7' />,
    url: "/messages",
  },
  {
    name: "Watchlists",
    description:
      "Add your favorite Investors and Startups to customized watchlists",
    icon: <Heart weight='duotone' className='text-orange-700 size-7' />,
    url: "/account/watchlist",
  },
  {
    name: "Bookmark",
    description: "Easily find your favorite “bookmarked” content here",
    icon: (
      <BookmarksSimple weight='duotone' className='text-orange-700 size-7' />
    ),
    url: "/account/bookmarks",
  },
  {
    name: "Matchmaking Tools",
    description:
      "Are you a Startup Founder looking for  the Perfect Investor? Or Vice Versa? This Matchmaking Tool is for you!",
    icon: <Fire weight='duotone' className='text-orange-700 size-7' />,
    url: "/investors",
  },
  {
    name: "Startup Events",
    description:
      "Plug-in to the Middle East & Africa’s biggest Startup Related Events",
    icon: <Ticket weight='duotone' className='text-orange-700 size-7' />,
    url: "/startup-events",
  },
  {
    name: "Newsletter Archive",
    description: "Go back and explore all of our weekly newsletters here",
    icon: (
      <NewspaperClipping weight='duotone' className='text-orange-700 size-7' />
    ),
    url: "/email-archive",
  },
  {
    name: "Startup Toolbox",
    description:
      "Get access to the best deals for startup resources, right here in our startup toolbox",
    icon: <PuzzlePiece weight='duotone' className='text-orange-700 size-7' />,
    url: "/toolbox",
  },
  {
    name: "Fundraising Readiness Test",
    description:
      "Thinking about fundraising? Take the free test to see if you're ready",
    icon: <Gauge weight='duotone' className='text-orange-700 size-7' />,
    url: "https://tally.so/r/GxW7Lo",
  },
];

export default function DrawerTools({ user }) {
  const [openLoginPopup, setOpenLoginPopup] = useAtom(loginPopup);
  const router = useRouter();
  return (
    <Drawer>
      <DrawerTrigger asChild>
        <div className='flex flex-col items-center gap-[3px]'>
          <button
            type='button'
            className='w-[60px] h-[60px] bg-white rounded-full shadow-md -mt-8 mb-1'
          >
            <span className='relative block h-8'>
              <Image
                src={NavLogo.src}
                alt='Lucidity Insights'
                layout='fill'
                objectFit='contain'
              />
            </span>
          </button>
          Tools
        </div>
      </DrawerTrigger>
      <DrawerContent className='text-sm font-medium tracking-tight text-left bg-white border-b border-background/10 text-background/50 rounded-t-3xl'>
        <div>
          <p className='pt-5 pb-2.5 text-sm font-medium tracking-tight text-center border-b border-background/10 text-background/50'>
            Tools & Resources
          </p>
          <div className='max-h-[85dvh] px-3 py-1 overflow-y-auto divide-y divide-background/10 divide-dashed'>
            {tools.map((tool, i) => {
              const isWatchlist = tool.url === "/account/watchlist";
              const isBookmark = tool.url === "/account/bookmarks";
              const isMessages = tool.url === "/messages";

              const handleClick = () => {
                if (user) {
                  router.push(tool.url);
                } else {
                  setOpenLoginPopup(true);
                }
              };

              const ToolContent = (
                <>
                  {tool.icon}
                  <div className='flex flex-col flex-1 gap-2'>
                    <span className='text-base font-semibold leading-none tracking-tight text-background'>
                      {tool.name}
                    </span>
                    <span className='text-[13px] leading-snug text-background/50'>
                      {tool.description}
                    </span>
                  </div>
                </>
              );

              return isWatchlist || isBookmark || isMessages ? (
                <button
                  key={i}
                  onClick={handleClick}
                  className='flex items-start w-full gap-3 py-3 text-left'
                >
                  {ToolContent}
                </button>
              ) : (
                <Link
                  key={i}
                  href={tool.url}
                  className='flex items-start gap-3 py-3'
                  {...(tool.url.startsWith("http") && {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  })}
                >
                  {ToolContent}
                </Link>
              );
            })}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
